export const DymekContextLimits=Object.freeze({
  wood:20,
  temperatures:12,
  events:12,
  history:5,
  privateTextChars:1200,
  preferences:20
});

function text(value,max=160){
  const result=String(value??'').trim();
  return result?result.slice(0,max):null;
}
function number(value){
  const n=Number(value);
  return Number.isFinite(n)?n:null;
}
function timestamp(value){
  const raw=String(value??'').trim();
  if(!raw)return null;
  const parsed=Date.parse(raw);
  return Number.isNaN(parsed)?null:new Date(parsed).toISOString();
}
function newest(rows=[],fields){
  const candidates=Array.isArray(fields)?fields:[fields];
  const value=row=>{
    for(const field of candidates){
      const parsed=Date.parse(row?.[field]||'');
      if(!Number.isNaN(parsed))return parsed;
    }
    return 0;
  };
  return [...(Array.isArray(rows)?rows:[])].sort((a,b)=>value(b)-value(a));
}
function limitedStrings(values=[],limit){
  return Object.freeze([...(Array.isArray(values)?values:[])]
    .map(value=>text(value,80)).filter(Boolean).slice(0,limit));
}
function profileContext(profile={},includePrivateText=false){
  if(!profile||typeof profile!=='object')return null;
  return Object.freeze({
    experienceLevel:text(profile.experienceLevel||profile.experience_level,40),
    smokerType:text(profile.smokerType||profile.smoker_type,60),
    preferredSmokeIntensity:text(
      profile.preferredSmokeIntensity||profile.preferred_smoke_intensity,
      40
    ),
    preferredWoodTypes:limitedStrings(
      profile.preferredWoodTypes||profile.preferred_wood_types,
      DymekContextLimits.preferences
    ),
    preferredFoodTypes:limitedStrings(
      profile.preferredFoodTypes||profile.preferred_food_types,
      DymekContextLimits.preferences
    ),
    defaultTargetTemperatureCelsius:number(
      profile.defaultTargetTemperatureC??profile.default_target_temperature_c
    ),
    ...(includePrivateText?{
      notes:text(profile.notes,DymekContextLimits.privateTextChars)
    }:{})
  });
}
function smokerContext(smoker,capabilities){
  if(!smoker&&!capabilities)return null;
  return Object.freeze({
    smokerType:text(smoker?.typeKey||smoker?.type||smoker?.smokerType,60),
    fuelType:text(smoker?.fuelType||smoker?.fuel_type,60),
    minTemperatureCelsius:number(capabilities?.minTemperatureCelsius??capabilities?.min_temperature_c),
    maxTemperatureCelsius:number(capabilities?.maxTemperatureCelsius??capabilities?.max_temperature_c),
    coldSmoking:text(capabilities?.coldSmoking||capabilities?.cold_smoking,20),
    coldSmokeMaxCelsius:number(capabilities?.coldSmokeMaxCelsius??capabilities?.cold_smoke_max_c),
    temperatureControl:text(capabilities?.temperatureControl||capabilities?.temperature_control,40),
    controlPrecisionCelsius:number(capabilities?.controlPrecisionCelsius??capabilities?.control_precision_c)
  });
}
function woodContext(rows=[]){
  return Object.freeze((Array.isArray(rows)?rows:[])
    .filter(row=>!row?.deletedAt)
    .slice(0,DymekContextLimits.wood)
    .map(row=>Object.freeze({
      woodKey:text(row.woodKey||row.wood_key||row.catalogueKey||row.catalogue_key,80),
      available:Boolean(
        row.available??row.isAvailable??row.inStock??(number(row.quantity??row.amount)!=null?number(row.quantity??row.amount)>0:true)
      ),
      favourite:Boolean(row.favourite??row.isFavourite)
    }))
    .filter(row=>row.woodKey));
}
function temperatureContext(rows=[]){
  return Object.freeze(newest(rows,['recordedAt','recorded_at'])
    .slice(0,DymekContextLimits.temperatures)
    .map(row=>Object.freeze({
      readingKind:text(row.readingKind||row.reading_kind,30),
      recordedAt:timestamp(row.recordedAt||row.recorded_at),
      valueCelsius:number(row.valueCelsius??row.value_c),
      sourceType:text(row.sourceType||row.source_type,40)
    }))
    .filter(row=>row.readingKind&&row.recordedAt&&row.valueCelsius!=null));
}
function eventContext(rows=[],includePrivateText=false){
  return Object.freeze(newest(rows,['occurredAt','occurred_at'])
    .slice(0,DymekContextLimits.events)
    .map(row=>Object.freeze({
      eventType:text(row.eventType||row.event_type,40),
      occurredAt:timestamp(row.occurredAt||row.occurred_at),
      ...(includePrivateText?{
        note:text(row.note,DymekContextLimits.privateTextChars),
        customText:text(row.customText||row.custom_text,DymekContextLimits.privateTextChars)
      }:{})
    }))
    .filter(row=>row.eventType&&row.occurredAt));
}
function outcomeSummary(outcome,includePrivateText){
  if(!outcome)return null;
  return Object.freeze({
    overallRating:number(outcome.overallRating??outcome.overall_rating),
    taste:number(outcome.taste??outcome.taste_rating),
    smoke:number(outcome.smoke??outcome.smoke_rating),
    juiciness:number(outcome.juiciness??outcome.juiciness_rating),
    texture:number(outcome.texture??outcome.texture_rating),
    appearance:number(outcome.appearance??outcome.appearance_rating),
    ...(includePrivateText?{
      lessonsLearned:text(outcome.lessonsLearned||outcome.lessons_learned,DymekContextLimits.privateTextChars)
    }:{})
  });
}
function historyContext(rows=[],includePrivateText=false){
  return Object.freeze(newest(rows,['completedAt','actualEndAt','actual_end_at'])
    .slice(0,DymekContextLimits.history)
    .map(row=>Object.freeze({
      sessionRef:text(row.sessionRef||row.id||row.sessionId,128),
      completedAt:timestamp(row.completedAt||row.actualEndAt||row.actual_end_at),
      recipeAttributed:Boolean(row.recipeId||row.recipe_id),
      plannerAttributed:Boolean(row.plannerScheduleSnapshot||row.planner_schedule_snapshot),
      outcome:outcomeSummary(row.outcome,includePrivateText)
    }))
    .filter(row=>row.sessionRef&&row.completedAt));
}
function plannerContext(snapshot){
  if(!snapshot||typeof snapshot!=='object')return null;
  return Object.freeze({
    targetReadyAt:timestamp(snapshot.targetReadyAt),
    timeZone:text(snapshot.timeZone,80),
    bufferMinutes:number(snapshot.bufferMinutes),
    compatibilityStatus:text(snapshot.compatibilityStatus,30),
    manualOverrideCount:Array.isArray(snapshot.blocks)
      ?snapshot.blocks.filter(block=>block?.origin==='manual').length
      :0
  });
}
function currentContext(current={},includePrivateText=false){
  const session=current.session||{};
  const stage=current.stage||null;
  return Object.freeze({
    session:Object.freeze({
      status:text(session.status,30),
      recipeAttributed:Boolean(session.recipeId||session.recipe_id),
      planner:plannerContext(session.plannerScheduleSnapshot||session.planner_schedule_snapshot),
      plannedStartAt:timestamp(session.plannedStartAt||session.planned_start_at),
      plannedEndAt:timestamp(session.plannedEndAt||session.planned_end_at),
      actualStartAt:timestamp(session.actualStartAt||session.actual_start_at),
      ...(includePrivateText?{title:text(session.title,DymekContextLimits.privateTextChars)}:{})
    }),
    stage:stage?Object.freeze({
      typeKey:text(stage.typeKey||stage.type_key,60),
      startedAt:timestamp(stage.startedAt||stage.activeStageStartedAt||stage.active_stage_started_at),
      targetChamberCelsius:number(stage.targetChamberCelsius??stage.target_chamber_c),
      minChamberCelsius:number(stage.minChamberCelsius??stage.min_chamber_c),
      maxChamberCelsius:number(stage.maxChamberCelsius??stage.max_chamber_c),
      plannedDurationMinutes:number(stage.plannedDurationMinutes??stage.planned_duration_minutes)
    }):null,
    recipe:current.recipe?Object.freeze({
      type:text(current.recipe.type,30),
      version:number(current.recipe.version),
      sourceType:text(current.recipe.sourceType||current.recipe.source_type,60),
      ...(includePrivateText?{title:text(current.recipe.title,DymekContextLimits.privateTextChars)}:{})
    }):null
  });
}

export function assembleDymekContext({
  profile=null,
  smoker=null,
  smokerCapabilities=null,
  availableWood=[],
  current={},
  temperatures=[],
  events=[],
  history=[],
  request={}
}={}){
  const includePrivateText=request?.privateTextNeeded===true;
  return Object.freeze({
    schemaVersion:1,
    privacy:Object.freeze({
      privateTextIncluded:includePrivateText,
      selectionMode:includePrivateText?'request_needed':'minimized'
    }),
    profile:profileContext(profile,includePrivateText),
    smoker:smokerContext(smoker,smokerCapabilities),
    wood:woodContext(availableWood),
    current:currentContext(current,includePrivateText),
    temperatures:temperatureContext(temperatures),
    events:eventContext(events,includePrivateText),
    history:historyContext(history,includePrivateText)
  });
}

export function inspectDymekContext(context={}){
  return Object.freeze({
    schemaVersion:Number(context.schemaVersion)||0,
    privateTextIncluded:Boolean(context?.privacy?.privateTextIncluded),
    hasProfile:Boolean(context.profile),
    hasSmoker:Boolean(context.smoker),
    woodCount:Array.isArray(context.wood)?context.wood.length:0,
    hasCurrentSession:Boolean(context?.current?.session?.status),
    hasCurrentStage:Boolean(context?.current?.stage),
    temperatureCount:Array.isArray(context.temperatures)?context.temperatures.length:0,
    eventCount:Array.isArray(context.events)?context.events.length:0,
    historyCount:Array.isArray(context.history)?context.history.length:0
  });
}
